import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { studentReviewReviewerRoles } from '@/auth/roles.js';
import { FormComboBox } from '@/components/forms/form-combo-box.js';
import { FormRadioGroup } from '@/components/forms/form-radio-group.js';
import { FormSingleSelect } from '@/components/forms/form-single-select.js';
import { FormTextArea } from '@/components/forms/form-text-area.js';
import { MultiSelectOption } from '@/components/ui/multi-select.js';
import {
  buildBooleanSelectOptions,
  buildNameLikeSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
} from '@/components/ui/multi-select-utils.js';
import { useAllLearningPaths, userTypeaheadSearch } from '@/lib/api-call-hooks.js';
import { formCreateStudentFeedbackSchema } from '@/lib/student-utils.js';
import { booleanFromString } from '@airreading/common';
import {
  StudentFeedbackIssue,
  StudentFeedbackIssueLabelMap,
  studentFeedbackIssueSchema,
  StudentFeedbackStatus,
  StudentFeedbackStatusLabelMap,
  studentFeedbackStatusSchema,
} from '@airreading/common/data';
import { useQueryClient } from '@tanstack/react-query';

export interface CreateStudentFeedbackFormProps {
  form: UseFormReturn<z.infer<typeof formCreateStudentFeedbackSchema>>;
}

export const CreateStudentFeedbackForm = ({ form }: CreateStudentFeedbackFormProps) => {
  const { watch, setValue } = form;
  const queryClient = useQueryClient();

  const studentFeedbackIssue = watch('studentFeedbackIssue');
  const relevelNeeded = booleanFromString(watch('relevelNeeded'));

  const { learningPaths: availableLearningPaths, isPending: isLoadingLearningPaths } = useAllLearningPaths();
  const learningPathOptions: MultiSelectOption[] = availableLearningPaths.map(buildNameLikeSelectOptions);

  const selectedComboLearningPathId = watch('learningPath')?.value;
  const selectedLearningPath = availableLearningPaths.find((lp) => lp.id === selectedComboLearningPathId);
  const selectableLessons: MultiSelectOption[] = (selectedLearningPath?.learningPathLessons || []).map(({ lesson }) =>
    buildNameLikeSelectOptions(lesson),
  );

  useEffect(() => {
    const sub = watch((value, { name }) => {
      if (name === 'studentFeedbackIssue' && value.studentFeedbackIssue !== StudentFeedbackIssue.learning_issue) {
        setValue('relevelNeeded', 'false');
      }

      if (name === 'relevelNeeded' && !booleanFromString(value.relevelNeeded)) {
        setValue('learningPath', emptySelectOption);
        setValue('startingLesson', emptySelectOption);
      }

      if (name === 'learningPath' && value.startingLesson?.value) {
        setValue('startingLesson', emptySelectOption, { shouldDirty: true, shouldValidate: true });
      }
    });
    return () => sub.unsubscribe();
  }, [setValue, watch, form]);

  return (
    <div className="space-y-4 py-4">
      <FormSingleSelect
        name="studentFeedbackIssue"
        label="Review type"
        form={form}
        options={studentFeedbackIssueSchema.options}
        renderOption={(value: StudentFeedbackIssue) => StudentFeedbackIssueLabelMap[value]}
        triggerProps={{ className: 'w-full' }}
      />
      {studentFeedbackIssue === StudentFeedbackIssue.learning_issue && (
        <FormRadioGroup
          form={form}
          label="Is relevel needed?"
          name="relevelNeeded"
          options={buildBooleanSelectOptions()}
        />
      )}
      {relevelNeeded && (
        <div className="grid grid-cols-2 gap-2">
          <FormComboBox
            form={form}
            label="Learning path"
            name="learningPath"
            placeholder="Select learning path"
            {...comboBoxOptionsOf(learningPathOptions)}
            triggerProps={{ className: 'w-full', disabled: isLoadingLearningPaths }}
          />
          <FormComboBox
            form={form}
            label="New starting lesson"
            name="startingLesson"
            placeholder="Select starting lesson"
            {...comboBoxOptionsOf(selectableLessons)}
            triggerProps={{ className: 'w-full', disabled: isLoadingLearningPaths }}
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <FormComboBox
          form={form}
          label="Admin"
          name="reviewer"
          placeholder="Select admin"
          {...comboBoxOptionsOf(userTypeaheadSearch(queryClient, { roles: [...studentReviewReviewerRoles] }))}
          triggerProps={{ className: 'w-full' }}
          triggerSearchOnFocus
        />
        <FormSingleSelect
          name="status"
          label="Issue status"
          form={form}
          options={studentFeedbackStatusSchema.options}
          renderOption={(value: StudentFeedbackStatus) => StudentFeedbackStatusLabelMap[value]}
          triggerProps={{ className: 'w-full' }}
        />
      </div>
      <FormTextArea name="note" label="Review note" form={form} />
    </div>
  );
};
